<script setup>
import {useTheme} from 'vuetify'
import ScrollToTop from '@core/components/ScrollToTop.vue'
import OrderNotification from '~/components/OrderNotification.vue'
import TableRequestNotification from '~/components/TableRequestNotification.vue'
import TabBillNotification from '~/components/TabBillNotification.vue'
import VersionUpdateDialog from '~/components/VersionUpdateDialog.vue'
import {useOrderEvents} from '~/composables/useOrderEvents'
import {useVersionChecker} from '~/composables/useVersionChecker'
import {useAppNotifications} from '~/composables/useAppNotifications'
import {useSystemWake} from '~/composables/useSystemWake'
import {useDeviceRegistration} from '~/composables/useDeviceRegistration'
import initCore from '@core/initCore'
import {initConfigStore, useConfigStore} from '@core/stores/config'
import {hexToRgb} from '@core/utils/colorConverter'

const {global} = useTheme()

// Initialize global notifications
useAppNotifications()

// ℹ️ Sync current theme with initial loader theme
initCore()
initConfigStore()

const configStore = useConfigStore()
const {isMobile} = useDevice()
const {isTabletMode: appIsTabletMode} = useTabletMode()
const {isLeader, startLeaderElection, stopLeaderElection} = usePosLeaderElection()
const deviceId = ref('pos')

if (isMobile && !appIsTabletMode.value)
  configStore.appContentLayoutNav = 'vertical'

const route = useRoute()
const appType = route.path.includes('/kiosk') ? 'kiosk' : 'pos'
const {
  showUpdateDialog,
  currentVersion,
  latestVersion,
  buildTimestamp,
  forceUpdate,
  applyUpdate,
  dismissUpdate
} = useVersionChecker(appType)

const handleHashNavigation = () => {
  if (typeof window === 'undefined' || !window.location) return false

  const hash = window.location.hash
  console.log('[app.vue] Hash navigation check:', hash)

  if (hash === '#customer') {
    console.log('[app.vue] Navigating to /customer')
    navigateTo('/customer', {replace: true})
    return true
  }
  if (hash === '#kiosk') {
    console.log('[app.vue] Navigating to /kiosk')
    navigateTo('/kiosk', {replace: true})
    return true
  }
  if (hash === '#kds') {
    console.log('[app.vue] Navigating to /kds')
    navigateTo('/kds', {replace: true})
    return true
  }
  if (hash === '#tablet') {
    console.log('[app.vue] Navigating to /orders (tablet)')
    navigateTo('/orders', {replace: true})
    return true
  }
  return false
}

const isSpecialWindow = ref(false)

if (import.meta.client) {
  isSpecialWindow.value = handleHashNavigation()
}

// Initialize socket connection globally
const config = useRuntimeConfig()
const userStore = useUserStore()
const socketStore = useSocketStore()
const {$sc: socket} = useNuxtApp()
const {subscribeToOrderEvents, fetchInitialOrders, handleIncomingEvent} = useOrderEvents()
const {
  pullOrdersFromBackend,
  saveLocalOrder,
  getLocalOrder,
  updateLocalOrderStatus,
  updateLocalOrderPaymentStatus,
  deleteLocalOrder,
  getInstanceId
} = usePosDatabase()
const {
  emitPosOrderSentToKitchen,
  emitPosOrderItemsAdded,
  emitPosOrderItemsRemoved,
  emitPosOrderStatusChanged,
  emitPosOrderItemReady,
  emitPosOrderItemServed,
  emitPosOrderReady,
  emitPosOrderDiscarded,
  emitPaymentCompleted,
  emitTableUpdated,
  emitTableOccupied,
  emitTableFreed,
  emitPosCourseFired,
  emitPosCourseHeld,
  emitKdsHeartbeat
} = usePosEventBus()
const {loadConfiguredPrinters, configuredPrinters} = usePrinter()
const { onWake, init: initSystemWake } = useSystemWake()
const {
  registrationStatus,
  isPendingApproval,
  isDisabled,
  registerDevice,
  checkStatus,
  startPollingForApproval,
  stopPolling,
  getAssignedTerminalId,
} = useDeviceRegistration()

const showPendingApprovalOverlay = ref(false)
const showDisabledOverlay = ref(false)

let syncInterval = null
let systemWakeCleanup = null
let posChannelSubscription = null
let tabsChannelSubscription = null

const scConfig = {
  hostname: config.public.scServer || 'localhost',
  port: config.public.scServerPort || 8081,
  secure: config.public.scServerSecure || false,
  autoConnect: config.public.scServerAutoConnect || true,
}

const updateLocalItemStatus = async (orderId, itemIndex, status, itemId = null) => {
  try {
    console.log(`[app.vue] 🔍 updateLocalItemStatus START: orderId=${orderId}, itemIndex=${itemIndex}, status=${status}, itemId=${itemId}`)
    const order = await getLocalOrder(orderId)
    if (!order || !order.orderDetails) {
      console.log(`[app.vue] ❌ Order ${orderId} not found or has no orderDetails. Order:`, order)
      return
    }

    console.log(`[app.vue] updateLocalItemStatus called: orderId=${orderId}, itemIndex=${itemIndex}, status=${status}, itemId=${itemId}`)
    console.log(`[app.vue] Order has ${order.orderDetails.length} items:`, order.orderDetails.map(i => ({
      id: i.id,
      name: i.menuItemName
    })))

    let updated = false
    if (itemId) {
      const numericItemId = Number(itemId)
      const idx = order.orderDetails.findIndex(item => Number(item.id) === numericItemId)
      if (idx !== -1) {
        order.orderDetails[idx].itemStatus = status
        updated = true
        console.log(`[app.vue] ✅ Updated item by ID ${itemId} (index ${idx}) status to ${status} for order ${orderId}`)
      } else {
        console.log(`[app.vue] ⚠️ Item ID ${itemId} not found in order ${orderId}`)
      }
    }
    if (!updated && itemIndex !== null && order.orderDetails[itemIndex]) {
      order.orderDetails[itemIndex].itemStatus = status
      updated = true
      console.log(`[app.vue] Updated item by index ${itemIndex} status to ${status} for order ${orderId}`)
    }
    if (updated) {
      await saveLocalOrder(order)
    } else {
      console.log(`[app.vue] ⚠️ No item was updated for order ${orderId}`)
    }
  } catch (err) {
    console.error('[app.vue] Failed to update local item status:', err)
  }
}

const updateLocalItemsStatusByIds = async (orderId, itemIds, status) => {
  try {
    const order = await getLocalOrder(orderId)
    if (!order || !order.orderDetails) return

    const itemIdSet = new Set(itemIds.map(id => Number(id)))
    let updated = 0
    order.orderDetails = order.orderDetails.map(item => {
      if (itemIdSet.has(Number(item.id))) {
        updated++
        return {...item, itemStatus: status}
      }
      return item
    })
    await saveLocalOrder(order)
    console.log(`[app.vue] Updated ${updated} items status to ${status} for order ${orderId}`)
  } catch (err) {
    console.error('[app.vue] Failed to update local items status:', err)
  }
}

const updateLocalItemNotes = async (orderId, orderDetailId, notes) => {
  try {
    const order = await getLocalOrder(orderId)
    if (!order || !order.orderDetails) return

    const itemIndex = order.orderDetails.findIndex(detail => detail.id === orderDetailId)
    if (itemIndex !== -1) {
      order.orderDetails[itemIndex].notes = notes
      await saveLocalOrder(order, '[updateLocalItemNotes]')
      console.log(`[app.vue] Updated item notes for order ${orderId}, detail ${orderDetailId}`)
    }
  } catch (err) {
    console.error('[app.vue] Failed to update local item notes:', err)
  }
}

const updateLocalOrderCourseStatus = async (orderId, courseId, courseStatus) => {
  try {
    const order = await getLocalOrder(orderId)
    console.log('[app.vue] updateLocalOrderCourseStatus - order found:', !!order, 'orderId:', orderId)
    if (!order || !order.orderDetails) {
      console.log('[app.vue] No order or orderDetails found for:', orderId)
      return
    }

    console.log('[app.vue] Order details courseIds:', order.orderDetails.map(i => ({courseId: i.courseId, course_id: i.course_id})))
    let updated = false
    order.orderDetails = order.orderDetails.map(item => {
      const itemCourseId = item.courseId || item.course_id
      if (Number(itemCourseId) === Number(courseId)) {
        updated = true
        return {...item, courseStatus, course_status: courseStatus}
      }
      return item
    })

    if (updated) {
      await saveLocalOrder(order)
      console.log('[app.vue] ✅ Updated course status locally:', orderId, courseId, courseStatus)
    } else if (order.enableCourseFireHold || order.enable_course_fire_hold) {
      console.log('[app.vue] ⚠️ No items matched courseId:', courseId, '- fetching fresh data from backend')
      try {
        const {data} = await useApi(`/api/pos/orders/${orderId}`)
        if (data.value?.order?.orderDetails) {
          order.orderDetails = data.value.order.orderDetails
          order.items = data.value.order.orderDetails
          await saveLocalOrder(order)
          console.log('[app.vue] ✅ Synced order details from backend with course data')
        }
      } catch (fetchErr) {
        console.error('[app.vue] Failed to fetch order from backend:', fetchErr)
      }
    }
  } catch (err) {
    console.error('[app.vue] Failed to update local course status:', err)
  }
}

const handlePosChannelMessage = async (eventName, payload) => {
  const normalizedEvent = (eventName || '').trim()
  console.log('[app.vue] 📨 POS channel message:', normalizedEvent, 'original:', eventName, 'type:', typeof eventName)

  try {
    if (normalizedEvent === 'roster:published') {
      console.log('[app.vue] 📅 Roster published - triggering staff data refresh')
      console.log(`[app.vue] Week: ${payload.data.weekStart} to ${payload.data.weekEnd}`)
      console.log(`[app.vue] ${payload.data.staffAddedCount} new staff added to business`)

      window.dispatchEvent(new CustomEvent('refresh-staff-list'))
      return
    }

    if (normalizedEvent === 'reservation:created' || normalizedEvent === 'reservation:status:changed') {
      console.log('[app.vue] 📅 Reservation event:', normalizedEvent, payload)
      const {useReservationsStore} = await import('@/stores/reservations')
      const reservationsStore = useReservationsStore()

      if (normalizedEvent === 'reservation:created') {
        reservationsStore.incrementPendingCount()
        console.log('[app.vue] New reservation created, pending count:', reservationsStore.pendingCount)
      } else if (normalizedEvent === 'reservation:status:changed') {
        if (payload.previousStatus === 'pending' && payload.status !== 'pending') {
          reservationsStore.decrementPendingCount()
          console.log('[app.vue] Reservation status changed from pending, count:', reservationsStore.pendingCount)
        }
      }

      window.dispatchEvent(new CustomEvent('refresh-reservations'))
      return
    }

    const isKioskControlEvent = normalizedEvent.startsWith('kiosk:') || normalizedEvent.startsWith('device:')
    if (isKioskControlEvent) {
      console.log('[app.vue] 📋 Kiosk control event detected:', normalizedEvent, payload)
      const {
        emitKioskDisableOrderTypes,
        emitKioskOrderingStatus,
        emitKioskRefreshMenu,
        emitKioskRequestPendingOrders,
        emitKioskRecoverOrder,
        emitDeviceDisabled,
        emitDeviceEnabled
      } = usePosEventBus()

      switch (normalizedEvent) {
        case 'kiosk:disable_order_types':
          emitKioskDisableOrderTypes(payload)
          break
        case 'kiosk:ordering_status':
          emitKioskOrderingStatus(payload)
          break
        case 'kiosk:refresh_menu':
        case 'kiosk:update_menu':
          emitKioskRefreshMenu(payload)
          break
        case 'kiosk:request_pending_orders':
          emitKioskRequestPendingOrders(payload)
          break
        case 'kiosk:recover_order':
          emitKioskRecoverOrder(payload)
          break
        case 'device:disabled':
          emitDeviceDisabled(payload)
          break
        case 'device:enabled':
          emitDeviceEnabled(payload)
          break
        default:
          console.log('[app.vue] Unknown kiosk control event:', normalizedEvent)
      }
      return
    }

    const posLocalId = payload.posLocalId || payload.guestSessionId
    const orderId = posLocalId || payload.orderId || payload.id || payload.backendOrderId

    const eventsWithoutOrderId = ['kds:heartbeat', 'patronect:customer_arrival', 'patronect:face_flagged']
    const isTabEvent = normalizedEvent.startsWith('tab:')
    if (!orderId && !eventsWithoutOrderId.includes(normalizedEvent) && !isTabEvent) {
      console.warn('[app.vue] ⚠️ No orderId in payload!', payload)
      return
    }

    if (isTabEvent) {
      console.log('[app.vue] 📋 Tab event received:', normalizedEvent, payload)
      window.dispatchEvent(new CustomEvent('tab-event', { detail: { event: normalizedEvent, payload } }))
      return
    }

    switch (normalizedEvent) {
      case 'pos:order:sent_to_kitchen':
        const {getInstanceId} = usePosDatabase()
        const sourceInstanceId = await getInstanceId()

        // Skip processing if this event came from the same device
        // The originating device already updated SQLite and emitted local events
        if (payload.sourceDeviceId && payload.sourceDeviceId === sourceInstanceId) {
          console.log('[app.vue] ⏭️ Skipping sent_to_kitchen from same device:', sourceInstanceId)
          break
        }

        console.log('[app.vue] Processing sent_to_kitchen event from other device:', orderId)
        let order = null

        // Fetch from backend (source of truth) for events from other devices
        if (payload.backendOrderId) {
          console.log('[app.vue] Fetching order from backend:', payload.backendOrderId)
          try {
            const backendOrder = await useApi(`/api/pos/orders/${payload.backendOrderId}?businessId=${userStore.activeBusinessId}&order_id=${payload.backendOrderId}&guest_session_id=${posLocalId}`, {}, {method: 'GET'}, 2)
            if (backendOrder && Object.keys(backendOrder).length > 0) {
              await saveLocalOrder(backendOrder)
              order = backendOrder
              console.log('[app.vue] ✅ Fetched and saved order from backend:', orderId)
            } else {
              console.warn('[app.vue] ⚠️ API response missing order data:', backendOrder)
            }
          } catch (fetchError) {
            console.error('[app.vue] Failed to fetch from backend:', fetchError)
            order = await getLocalOrder(orderId)
            if (order) {
              console.log('[app.vue] Using local order as fallback:', orderId)
            }
          }
        } else {
          order = await getLocalOrder(orderId)
        }

        if (order) {
          // Wait for SQLite write to complete before emitting
          await new Promise(resolve => setTimeout(resolve, 1500))
          emitPosOrderSentToKitchen(orderId, order.orderNumber)
          console.log('[app.vue] Order sent to kitchen:', orderId)

          // Main POS terminal should print kitchen receipt
          if (!appIsTabletMode.value) {
            console.log('[app.vue] 🖨️ Not tablet mode, checking if main POS terminal...')
            try {
              const {printKitchenReceipt, isMainPosTerminal, loadConfiguredPrinters: loadPrinters, configuredPrinters: printers} = usePrinter()

              if (printers.value.length === 0) {
                console.log('[app.vue] 🖨️ Loading configured printers...')
                await loadPrinters()
              }
              console.log('[app.vue] 🖨️ Configured printers:', printers.value.length, printers.value.map(p => ({name: p.friendly_name, dest: p.destination, enabled: p.is_enabled})))

              const isMainPos = await isMainPosTerminal()
              console.log('[app.vue] 🖨️ Is main POS terminal?', isMainPos)

              if (isMainPos) {
                console.log('[app.vue] 🖨️ Calling printKitchenReceipt with order:', {
                  orderId: order.id,
                  orderNumber: order.orderNumber,
                  itemCount: order.items?.length || order.orderDetails?.length || 0
                })
                const printResult = await printKitchenReceipt(order)
                console.log('[app.vue] 🖨️ Print result:', printResult)

                if (printResult.success) {
                  console.log('[app.vue] ✅ Printed kitchen receipt for remote order:', orderId)

                  // Confirm print to backend - mark items as printed
                  if (payload.backendOrderId) {
                    try {
                      const printedItemIds = (order.orderDetails || order.items || [])
                        .map(item => item.id)
                        .filter(Boolean)

                      if (printedItemIds.length > 0) {
                        const {getInstanceId} = usePosDatabase()
                        const sourceInstanceId = await getInstanceId()

                        await useApi(`/api/pos/orders/${payload.backendOrderId}/confirm-print`, {
                          orderDetailIds: printedItemIds,
                          businessId: userStore.activeBusinessId,
                          sourceDevice: 'pos',
                          sourceInstanceId,
                          guest_session_id: orderId,
                          order_id: payload.backendOrderId
                        }, {method: 'POST'}, 2)

                        console.log('[app.vue] ✅ Confirmed print for', printedItemIds.length, 'items')

                        // Update local order items with printed status
                        const printedIdSet = new Set(printedItemIds)
                        order.orderDetails = order.orderDetails.map(item => {
                          if (printedIdSet.has(item.id)) {
                            return {...item, itemStatus: 'printed', printedAt: new Date().toISOString()}
                          }
                          return item
                        })
                        order.items = order.orderDetails
                        await saveLocalOrder(order)
                        console.log('[app.vue] Updated local order with printed status')
                      }
                    } catch (confirmError) {
                      console.error('[app.vue] ❌ Failed to confirm print:', confirmError)
                    }
                  }
                } else {
                  console.warn('[app.vue] ⚠️ Kitchen printer not configured:', printResult.message)
                }
              } else {
                console.log('[app.vue] ⏭️ Skipping kitchen print (not Main POS Terminal)')
              }
            } catch (printError) {
              console.error('[app.vue] ❌ Failed to print kitchen receipt for remote order:', printError)
            }
          } else {
            console.log('[app.vue] ⏭️ Skipping kitchen print (device is in tablet mode)')
          }
        } else {
          console.warn('[app.vue] ⚠️ No order found after fetch attempt')
        }
        break

      case 'pos:order:items_added':
        // Skip processing if this event came from the same device
        if (payload.sourceDeviceId && payload.sourceDeviceId === deviceId.value) {
          console.log('[app.vue] ⏭️ Skipping items_added from same device:', deviceId.value)
          break
        }

        console.log('[app.vue] Processing items_added event from other device:', orderId)

        // Update local SQLite from backend (source of truth)
        if (payload.data) {
          console.log('[app.vue] 🔄 Saving order from payload.data')
          await saveLocalOrder(payload.data)
          console.log('[app.vue] ✅ Saved order from items_added event:', orderId)
        } else if (payload.backendOrderId) {
          console.log('[app.vue] Fetching order from backend:', payload.backendOrderId)
          try {
            const backendOrder = await useApi(`/api/pos/orders/${payload.backendOrderId}?businessId=${userStore.activeBusinessId}&order_id=${payload.backendOrderId}&guest_session_id=${posLocalId}`, {}, {method: 'GET'}, 2)
            if (backendOrder?.id) {
              await saveLocalOrder(backendOrder)
              console.log('[app.vue] ✅ Fetched and saved order after items_added event:', orderId)
            }
          } catch (fetchError) {
            console.error('[app.vue] Failed to fetch order after items_added:', fetchError)
          }
        }

        // Wait for SQLite write to complete before emitting
        await new Promise(resolve => setTimeout(resolve, 1500))
        emitPosOrderItemsAdded(orderId, payload.data?.orderDetails || payload.data?.items || [])

        // Main POS terminal should print incremental kitchen receipt
        if (!appIsTabletMode.value && payload.newItems && payload.newItems.length > 0) {
          console.log('[app.vue] 🖨️ Checking if should print incremental kitchen receipt...')
          try {
            const {printIncrementalKitchenReceipt, isMainPosTerminal, loadConfiguredPrinters: loadPrinters, configuredPrinters: printers} = usePrinter()

            if (printers.value.length === 0) {
              console.log('[app.vue] 🖨️ Loading configured printers for incremental print...')
              await loadPrinters()
            }

            const isMainPos = await isMainPosTerminal()

            if (isMainPos) {
              const orderToPrint = payload.data || await getLocalOrder(orderId)
              if (orderToPrint) {
                console.log('[app.vue] 🖨️ Printing incremental kitchen receipt for', payload.newItems.length, 'new items')
                const printResult = await printIncrementalKitchenReceipt(orderToPrint, payload.newItems)

                if (printResult.success) {
                  console.log('[app.vue] ✅ Incremental kitchen receipt printed')

                  // Confirm print to backend - mark items as printed
                  try {
                    const printedItemIds = payload.newItems.map(item => item.id).filter(Boolean)
                    if (printedItemIds.length > 0) {
                      const {getInstanceId} = usePosDatabase()
                      const sourceInstanceId = await getInstanceId()

                      await useApi(`/api/pos/orders/${payload.backendOrderId}/confirm-print`, {
                        orderDetailIds: printedItemIds,
                        businessId: userStore.activeBusinessId,
                        sourceDevice: 'pos',
                        sourceInstanceId,
                        guest_session_id: orderId,
                        order_id: payload.backendOrderId
                      }, {method: 'POST'}, 2)

                      console.log('[app.vue] ✅ Confirmed print for', printedItemIds.length, 'items')

                      // Update local order items with printed status
                      const printedIdSet = new Set(printedItemIds)
                      const updatedOrder = await getLocalOrder(orderId)
                      if (updatedOrder) {
                        updatedOrder.orderDetails = updatedOrder.orderDetails.map(item => {
                          if (printedIdSet.has(item.id)) {
                            return {...item, itemStatus: 'printed', printedAt: new Date().toISOString()}
                          }
                          return item
                        })
                        updatedOrder.items = updatedOrder.orderDetails
                        await saveLocalOrder(updatedOrder)
                        console.log('[app.vue] Updated local order with printed status')
                      }
                    }
                  } catch (confirmError) {
                    console.error('[app.vue] ❌ Failed to confirm print:', confirmError)
                  }
                } else {
                  console.warn('[app.vue] ⚠️ Incremental kitchen print failed:', printResult.message)
                }
              }
            } else {
              console.log('[app.vue] ⏭️ Skipping incremental print (not Main POS Terminal)')
            }
          } catch (printError) {
            console.error('[app.vue] ❌ Failed to print incremental kitchen receipt:', printError)
          }
        }
        break

      case 'pos:order:items_removed':
        // Skip processing if this event came from the same device
        if (payload.sourceDeviceId && payload.sourceDeviceId === deviceId.value) {
          console.log('[app.vue] ⏭️ Skipping items_removed from same device:', deviceId.value)
          break
        }

        console.log('[app.vue] Processing items_removed event from other device:', orderId)

        // Fetch from backend (source of truth)
        if (payload.backendOrderId) {
          try {
            const backendOrder = await useApi(`/api/pos/orders/${payload.backendOrderId}?businessId=${userStore.activeBusinessId}&order_id=${payload.backendOrderId}&guest_session_id=${posLocalId}`, {}, {method: 'GET'}, 2)
            if (backendOrder?.id) {
              await saveLocalOrder(backendOrder)
              console.log('[app.vue] ✅ Updated local order from backend after items_removed:', orderId)

              // Print removed items receipt
              if (!appIsTabletMode.value && payload.removedItems && payload.removedItems.length > 0) {
                try {
                  const {printRemovedItemsKitchenReceipt, isMainPosTerminal, loadConfiguredPrinters: loadPrinters, configuredPrinters: printers} = usePrinter()

                  if (printers.value.length === 0) {
                    console.log('[app.vue] 🖨️ Loading configured printers for removed items print...')
                    await loadPrinters()
                  }

                  const isMainPos = await isMainPosTerminal()

                  if (isMainPos) {
                    console.log('[app.vue] 🖨️ Printing removed items kitchen receipt')
                    const printResult = await printRemovedItemsKitchenReceipt(backendOrder, payload.removedItems)
                    if (printResult.success) {
                      console.log('[app.vue] ✅ Removed items kitchen receipt printed')
                    } else {
                      console.warn('[app.vue] ⚠️ Removed items print failed:', printResult.message)
                    }
                  }
                } catch (printErr) {
                  console.error('[app.vue] ❌ Removed items print error:', printErr)
                }
              }
            }
          } catch (fetchError) {
            console.error('[app.vue] Failed to fetch order after items_removed:', fetchError)
          }
        }
        // Wait for SQLite write to complete before emitting
        await new Promise(resolve => setTimeout(resolve, 1500))
        emitPosOrderItemsRemoved(orderId, payload.items || [])
        console.log('[app.vue] Items removed from order:', orderId)
        break

      case 'pos:order:items_print_confirmed':
        console.log('[app.vue] Items print confirmed event received:', payload)
        if (payload.backendOrderId) {
          try {
            // Wait a moment for DB transaction to fully commit
            await new Promise(resolve => setTimeout(resolve, 500))

            const backendOrder = await useApi(`/api/pos/orders/${payload.backendOrderId}?businessId=${userStore.activeBusinessId}&order_id=${payload.backendOrderId}&guest_session_id=${posLocalId}`, {}, {method: 'GET'}, 2)
            if (backendOrder?.id) {
              console.log('[app.vue] 📦 Fetched order from backend:', {
                orderId: backendOrder.id,
                itemsCount: backendOrder.orderDetails?.length || 0,
                firstItemStatus: backendOrder.orderDetails?.[0]?.itemStatus || 'unknown'
              })
              await saveLocalOrder(backendOrder)
              console.log('[app.vue] ✅ Updated local order from backend after items_print_confirmed:', orderId)

              const {emitPosOrderUpdated} = usePosEventBus()
              await new Promise(resolve => setTimeout(resolve, 1500))
              emitPosOrderUpdated(backendOrder)
              console.log('[app.vue] 📡 Emitted POS_ORDER_UPDATED event for:', orderId)
            }
          } catch (fetchError) {
            console.error('[app.vue] Failed to fetch order after items_print_confirmed:', fetchError)
          }
        }
        break

      case 'pos:order:status_changed':
        // Skip processing if this event came from the same device
        if (payload.sourceDeviceId && payload.sourceDeviceId === deviceId.value) {
          console.log('[app.vue] ⏭️ Skipping status_changed from same device:', deviceId.value)
          break
        }
        console.log('[app.vue] Processing status_changed event from other device:', orderId)
        await updateLocalOrderStatus(orderId, payload.status)
        await new Promise(resolve => setTimeout(resolve, 1500))
        emitPosOrderStatusChanged(orderId, payload.status)
        console.log('[app.vue] Updated order status:', orderId, payload.status)
        break

      case 'pos:order:item_ready':
        console.log('[app.vue] pos:order:item_ready compare source ids: ', payload.sourceDeviceId, deviceId.value, " ====")
        // Skip processing if this event came from the same device
        if (payload.sourceDeviceId && payload.sourceDeviceId === deviceId.value) {
          console.log('[app.vue] ⏭️ Skipping item_ready from same device:', deviceId.value)
          break
        }
        console.log('[app.vue] Processing item_ready event from other device:', orderId)
        await updateLocalItemStatus(orderId, payload.itemIndex, 'ready', payload.itemId)
        await new Promise(resolve => setTimeout(resolve, 1500))
        emitPosOrderItemReady(orderId, payload.itemIndex, payload.itemName, payload.orderNumber)
        console.log('[app.vue] Item ready:', orderId, payload.itemIndex, 'itemId:', payload.itemId)
        break

      case 'pos:order:items_ready':
        // Bulk items ready (e.g., from KDS "Mark Add-ons Ready" button)
        if (payload.sourceDeviceId && payload.sourceDeviceId === deviceId.value) {
          console.log('[app.vue] ⏭️ Skipping items_ready from same device:', deviceId.value)
          break
        }
        console.log('[app.vue] Processing items_ready event from other device:', orderId, 'itemIds:', payload.itemIds)
        await updateLocalItemsStatusByIds(orderId, payload.itemIds, 'ready')
        await new Promise(resolve => setTimeout(resolve, 1500))
        for (const itemId of (payload.itemIds || [])) {
          emitPosOrderItemReady(orderId, null, null, payload.orderNumber)
        }
        console.log('[app.vue] Items ready:', orderId, payload.itemIds?.length, 'items')
        break

      case 'pos:order:item_served':
        // Skip processing if this event came from the same device
        if (payload.sourceDeviceId && payload.sourceDeviceId === deviceId.value) {
          console.log('[app.vue] ⏭️ Skipping item_served from same device:', deviceId.value)
          break
        }
        console.log('[app.vue] Processing item_served event from other device:', orderId)
        await updateLocalItemStatus(orderId, payload.itemIndex, 'served', payload.itemId)
        await new Promise(resolve => setTimeout(resolve, 1500))
        emitPosOrderItemServed(orderId, payload.itemIndex, payload.itemName, payload.orderNumber)
        console.log('[app.vue] Item served:', orderId, payload.itemIndex, 'itemId:', payload.itemId)
        break

      case 'pos:order:ready':
        // This is a notification event only - status is already updated via pos:order:status_changed
        // Skip if from same device (originator already handled it)
        if (payload.sourceDeviceId && payload.sourceDeviceId === deviceId.value) {
          console.log('[app.vue] ⏭️ Skipping order_ready notification from same device:', deviceId.value)
          break
        }
        console.log('[app.vue] Order ready notification from other device:', orderId)
        break

      case 'pos:order:item_notes_updated':
        // Skip processing if this event came from the same device
        if (payload.sourceDeviceId && payload.sourceDeviceId === deviceId.value) {
          console.log('[app.vue] ⏭️ Skipping item_notes_updated from same device:', deviceId.value)
          break
        }
        console.log('[app.vue] Processing item_notes_updated event from other device:', orderId)
        await updateLocalItemNotes(orderId, payload.orderDetailId, payload.notes)
        await new Promise(resolve => setTimeout(resolve, 1500))
        emitPosOrderUpdated(orderId)
        console.log('[app.vue] ✅ Item notes updated:', orderId, payload.orderDetailId)
        break

      case 'payment:status_updated':
        console.log('[app.vue] 💳 Payment status updated:', payload)
        window.dispatchEvent(new CustomEvent('payment-status-updated', {detail: payload}))
        break

      case 'pos:course:fired':
        console.log('[app.vue] 🔥 Course fired:', payload)
        if (payload.sourceDeviceId && payload.sourceDeviceId === deviceId.value) {
          console.log('[app.vue] ⏭️ Skipping course:fired from same device:', deviceId.value)
          break
        }
        console.log('[app.vue] 🔥 Updating local order then emitting...')
        try {
          await updateLocalOrderCourseStatus(payload.orderId, payload.courseId, 'fired')
          console.log('[app.vue] 🔥 updateLocalOrderCourseStatus completed')
        } catch (e) {
          console.error('[app.vue] 🔥 updateLocalOrderCourseStatus error:', e)
        }
        emitPosCourseFired(payload.orderId, payload.courseId, payload.courseName)
        break

      case 'pos:course:held':
        console.log('[app.vue] ⏸️ Course held:', payload)
        if (payload.sourceDeviceId && payload.sourceDeviceId === deviceId.value) {
          console.log('[app.vue] ⏭️ Skipping course:held from same device:', deviceId.value)
          break
        }
        await updateLocalOrderCourseStatus(payload.orderId, payload.courseId, 'held')
        emitPosCourseHeld(payload.orderId, payload.courseId, payload.courseName)
        break

      case 'kds:heartbeat':
        console.log('[app.vue] 💓 KDS heartbeat event received:', payload.deviceId)
        emitKdsHeartbeat(payload.deviceId, payload.businessId)
        break

      case 'pos:order:discarded':
        // Skip processing if this event came from the same device
        if (payload.sourceDeviceId && payload.sourceDeviceId === deviceId.value) {
          console.log('[app.vue] ⏭️ Skipping discarded from same device:', deviceId.value)
          break
        }
        console.log('[app.vue] Processing discarded event from other device:', orderId)
        const discardedOrder = await getLocalOrder(orderId)
        if (discardedOrder) {
          await deleteLocalOrder(orderId)
          console.log('[app.vue] ✅ Deleted discarded order from local DB:', orderId)
        }
        emitPosOrderDiscarded(orderId, payload.tableId, payload.orderType)

        if (payload.tableId && payload.orderType === 'Dine In') {
          emitTableFreed(payload.tableId, null)
          console.log('[app.vue] 🪑 Table freed:', payload.tableId)
        }
        break

      case 'order:cancelled':
        console.log('[app.vue] ❌ Order cancelled:', payload)
        if (payload.sourceDeviceId && payload.sourceDeviceId === deviceId.value) {
          console.log('[app.vue] ⏭️ Skipping cancelled from same device:', deviceId.value)
          break
        }
        const cancelledOrder = await getLocalOrder(payload.orderId)
        if (cancelledOrder) {
          cancelledOrder.status = 'cancelled'
          await saveLocalOrder(cancelledOrder)
          console.log('[app.vue] ✅ Updated order status to cancelled:', payload.orderId)
        }
        emitPosOrderStatusChanged(payload.orderId, 'cancelled')
        break

      case 'pos:order:runner':
        // Skip processing if this event came from the same device
        if (payload.sourceDeviceId && payload.sourceDeviceId === deviceId.value) {
          console.log('[app.vue] ⏭️ Skipping runner from same device:', deviceId.value)
          break
        }
        console.log('[app.vue] Processing runner event from other device:', orderId)
        const runnerOrder = await getLocalOrder(orderId)
        if (runnerOrder) {
          runnerOrder.status = 'runner'
          runnerOrder.isRunner = true
          await saveLocalOrder(runnerOrder)
          console.log('[app.vue] ✅ Updated runner order status in local DB:', orderId)
        }
        emitPosOrderStatusChanged(orderId, 'runner')
        break

      case 'order_payment_completed':
        // Skip processing if this event came from the same device
        if (payload.sourceDeviceId && payload.sourceDeviceId === deviceId.value) {
          console.log('[app.vue] ⏭️ Skipping payment_completed from same device:', deviceId.value)
          break
        }
        // Prioritize backend IDs for lookup - tablets store orders by backend_order_id
        const backendId = payload.order?.backendOrderId || payload.order?.backend_order_id || payload.orderId || payload.order?.id
        console.log('[app.vue] Processing payment_completed event from other device:', orderId, 'backendId:', backendId)
        // Try to find order by backend ID first (most reliable for cross-device sync)
        let paidOrder = null
        if (backendId) {
          console.log('[app.vue] Trying to find order by backendId:', backendId)
          paidOrder = await getLocalOrder(backendId)
        }
        if (!paidOrder && orderId) {
          console.log('[app.vue] Trying to find order by orderId:', orderId)
          paidOrder = await getLocalOrder(orderId)
        }
        if (paidOrder) {
          paidOrder.status = 'completed'
          paidOrder.paymentStatus = 'paid'
          paidOrder.paymentMethod = payload.paymentMethod
          paidOrder.totalAmount = payload.totalAmount || paidOrder.totalAmount
          await saveLocalOrder(paidOrder)
          console.log('[app.vue] ✅ Updated paid order status in local DB:', paidOrder.id)

          // Free table if dine-in order
          if (paidOrder.tableId && paidOrder.orderType === 'dine_in') {
            emitTableFreed(paidOrder.tableId, paidOrder.tableName)
            console.log('[app.vue] 🪑 Table freed after payment:', paidOrder.tableId)
          }
        } else {
          console.log('[app.vue] ⚠️ Paid order not found locally:', orderId, 'backendId:', backendId)
        }
        // Emit event bus notification so tablets/KDS can update UI
        // Use the actual order ID if found, otherwise fall back to backend ID
        const paidOrderId = paidOrder?.id || backendId || orderId
        emitPaymentCompleted(paidOrderId, payload.totalAmount, payload.paymentMethod)
        break

      case 'order:new':
      case 'new_order':
        // 'order:new' is published by orders_controller for ASAP online orders.
        // 'new_order' is published by the orders_dispatch_scheduled cron when a scheduled order
        // enters its 30-minute notification window. Both must surface to POS identically.
        console.log('[app.vue] 🛒 New online order received:', payload.id || payload.orderId)
        await handleIncomingEvent('new_order', payload)
        break

      case 'kds:order:refresh':
        console.log('[app.vue] 📺 KDS order refresh received:', payload.backendOrderId)
        const {emitKdsOrderRefresh} = usePosEventBus()
        emitKdsOrderRefresh(payload.order)
        break

      case 'pos:order:accepted':
        console.log('[app.vue] ✅ Order accepted:', payload.orderId, 'status:', payload.status)
        if (payload.backendOrderId) {
          try {
            const backendOrder = await useApi(`/api/pos/orders/${payload.backendOrderId}?businessId=${userStore.activeBusinessId}`, {}, {method: 'GET'}, 2)
            if (backendOrder?.id) {
              await saveLocalOrder(backendOrder)
              console.log('[app.vue] ✅ Fetched and saved accepted order:', payload.backendOrderId)
              const {emitKdsOrderRefresh: emitRefresh} = usePosEventBus()
              emitRefresh(backendOrder)
            }
          } catch (fetchError) {
            console.error('[app.vue] Failed to fetch accepted order:', fetchError)
          }
        }
        break

      case 'payment:captured':
        console.log('[app.vue] 💳 Payment captured:', payload.orderId)
        const {emitPaymentCaptured} = usePosEventBus()
        emitPaymentCaptured(payload.orderId, payload)
        if (payload.orderId) {
          try {
            const capturedOrder = await useApi(`/api/pos/orders/${payload.orderId}?businessId=${userStore.activeBusinessId}`, {}, {method: 'GET'}, 2)
            if (capturedOrder?.id) {
              await saveLocalOrder(capturedOrder)
              console.log('[app.vue] ✅ Fetched and saved order after payment capture:', payload.orderId)
              const {emitKdsOrderRefresh: emitRefreshCaptured} = usePosEventBus()
              emitRefreshCaptured(capturedOrder)
            }
          } catch (fetchError) {
            console.error('[app.vue] Failed to fetch order after payment capture:', fetchError)
          }
        }
        break

      case 'patronect:customer_arrival':
        console.log('[app.vue] 👤 Customer arrival detected:', payload)
        const {emitPatronectCustomerArrival} = usePosEventBus()
        emitPatronectCustomerArrival(payload)
        break

      case 'patronect:face_flagged':
        console.log('[app.vue] 🚨 Face flagged as runner:', payload)
        const {emitPatronectFaceFlagged} = usePosEventBus()
        emitPatronectFaceFlagged(payload)
        break

      case 'table:status:changed':
        console.log('[app.vue] 🪑 Table status changed:', payload)
        emitTableUpdated(payload.tableId, payload.tableName, payload.status)
        if (payload.status === 'occupied') {
          emitTableOccupied(payload.tableId, payload.tableName)
        } else if (payload.status === 'available') {
          emitTableFreed(payload.tableId, payload.tableName)
        }
        break

      case 'table:request:new':
        console.log('[app.vue] 🖐️ New table request:', payload)
        const {emitTableRequestNew} = usePosEventBus()
        emitTableRequestNew(payload)
        break

      case 'table:request:acknowledged':
        console.log('[app.vue] ✅ Table request acknowledged:', payload)
        const {emitTableRequestAcknowledged} = usePosEventBus()
        emitTableRequestAcknowledged(payload)
        break

      case 'tab:bill_requested':
        console.log('[app.vue] 🧾 Tab bill requested:', payload)
        const {emitTabBillRequested} = usePosEventBus()
        emitTabBillRequested(payload)
        break

      case 'tab:payment_received':
        console.log('[app.vue] 💳 Tab payment received:', payload)
        const {emitTabPaymentReceived} = usePosEventBus()
        emitTabPaymentReceived(payload)
        if (payload.orderId) {
          const newPaymentStatus = payload.allPaid ? 'paid' : 'partial'
          await updateLocalOrderPaymentStatus(payload.orderId, newPaymentStatus)
          emitPaymentCompleted(payload.orderId, payload.amount, 'online_card')
        }
        break

      case 'kiosk:pending_orders':
        console.log('[app.vue] 📋 Kiosk pending orders received:', payload)
        const {emitKioskPendingOrders} = usePosEventBus()
        emitKioskPendingOrders(payload)
        break

      case 'kiosk:order_recovery_result':
        console.log('[app.vue] 📋 Kiosk order recovery result:', payload)
        const {emitKioskOrderRecoveryResult} = usePosEventBus()
        emitKioskOrderRecoveryResult(payload)
        break

      default:
        console.warn('[app.vue] Unknown POS event:', normalizedEvent, 'charCodes:', [...normalizedEvent].map(c => c.charCodeAt(0)))
    }
  } catch (err) {
    console.error('[app.vue] Failed to handle POS message:', err)
  }
}

const handleKdsChannelMessage = async (eventName, payload) => {
  console.log('[app.vue] 📨 KDS channel message:', eventName, payload)

  try {
    const orderId = payload.orderId || payload.posLocalId

    if (!orderId) {
      console.warn('[app.vue] ⚠️ No orderId in payload!', payload)
      return
    }

    console.log('[app.vue] Processing KDS event:', {
      event: eventName,
      orderId,
      backendOrderId: payload.backendOrderId,
      posLocalId: payload.posLocalId,
      status: payload.status
    })

    switch (eventName) {
      case 'pos:order:status_changed':
        // Skip processing if this event came from the same device
        if (payload.sourceDeviceId && payload.sourceDeviceId === deviceId.value) {
          console.log('[app.vue] ⏭️ Skipping KDS status_changed from same device:', deviceId.value)
          break
        }
        console.log('[app.vue] KDS: Processing status_changed from other device:', orderId)
        await updateLocalOrderStatus(orderId, payload.status)
        emitPosOrderStatusChanged(orderId, payload.status)
        console.log('[app.vue] ✅ Order status changed (KDS):', orderId, payload.status)
        break

      case 'pos:order:ready':
        // Skip if from same device (originator already handled it)
        if (payload.sourceDeviceId && payload.sourceDeviceId === deviceId.value) {
          console.log('[app.vue] ⏭️ Skipping KDS order_ready from same device:', deviceId.value)
          break
        }
        // This is a notification event only - status is already updated via pos:order:status_changed
        emitPosOrderReady(orderId, payload.orderNumber, payload.orderType, payload.tableNumber)
        console.log('[app.vue] Order ready notification (KDS channel):', orderId)
        break

      case 'pos:order:discarded':
        // Skip processing if this event came from the same device
        if (payload.sourceDeviceId && payload.sourceDeviceId === deviceId.value) {
          console.log('[app.vue] ⏭️ Skipping KDS discarded from same device:', deviceId.value)
          break
        }
        console.log('[app.vue] KDS: Processing discarded from other device:', orderId)
        const discardedKdsOrder = await getLocalOrder(orderId)
        if (discardedKdsOrder) {
          await deleteLocalOrder(orderId)
          console.log('[app.vue] ✅ Deleted discarded order from local DB (KDS):', orderId)
        }
        emitPosOrderDiscarded(orderId, payload.tableId, payload.orderType)

        if (payload.tableId && payload.orderType === 'Dine In') {
          emitTableFreed(payload.tableId, null)
          console.log('[app.vue] 🪑 Table freed (KDS):', payload.tableId)
        }
        break

      default:
        console.warn('[app.vue] Unknown KDS event:', eventName)
    }
  } catch (err) {
    console.error('[app.vue] Failed to handle KDS message:', err)
  }
}

const subscribeToChannels = async () => {
  if (!userStore.activeBusinessId) {
    console.log('[app.vue] Channel subscription skipped: No active business')
    return
  }

  deviceId.value = await getInstanceId()
  console.log('[app.vue] 🔧 Device ID:', deviceId.value)

  console.log('[app.vue] 📡 Subscribing to POS + tabs channels for all devices')
  await subscribeToPosChannel()
  await subscribeToTabsChannel()
}


const subscribeToPosChannel = async () => {
  if (posChannelSubscription) {
    console.log('[app.vue] Already subscribed to POS channel')
    return
  }

  try {
    const posChannel = `business-${userStore.activeBusinessId}-pos`
    console.log('[app.vue] 🔔 Subscribing to POS channel:', posChannel)

    const {error, businessChannel} = await socket.subscribe(posChannel, (eventName, payload) => {
      handlePosHubMessage(eventName, payload)
    })

    if (error) {
      console.error('[app.vue] ❌ Failed to subscribe to POS channel:', error)
    } else {
      posChannelSubscription = businessChannel
      console.log('[app.vue] ✅ Subscribed to POS channel successfully')
    }
  } catch (error) {
    console.error('[app.vue] Failed to subscribe to POS channel:', error)
  }
}


const subscribeToTabsChannel = async () => {
  if (tabsChannelSubscription) {
    console.log('[app.vue] Already subscribed to tabs channel')
    return
  }

  try {
    const tabsChannel = `business-${userStore.activeBusinessId}-tabs`
    console.log('[app.vue] 🔔 Subscribing to tabs channel:', tabsChannel)

    const {error, businessChannel} = await socket.subscribe(tabsChannel, (eventName, payload) => {
      handlePosHubMessage(eventName, payload)
    })

    if (error) {
      console.error('[app.vue] ❌ Failed to subscribe to tabs channel:', error)
    } else {
      tabsChannelSubscription = businessChannel
      console.log('[app.vue] ✅ Subscribed to tabs channel successfully')
    }
  } catch (error) {
    console.error('[app.vue] Failed to subscribe to tabs channel:', error)
  }
}


const handlePosHubMessage = async (eventName, payload) => {
  console.log('[app.vue] 📨 Received from POS channel:', eventName, payload)

  const actualPayload = payload.payload || payload
  const sourceInstanceId = actualPayload.sourceDeviceId || payload.sourceDeviceId

  if (sourceInstanceId) {
    const {getInstanceId} = usePosDatabase()
    const myInstanceId = await getInstanceId()

    if (sourceInstanceId === myInstanceId) {
      console.log(`[app.vue] ⏭️ Skipping event from own device instance (${sourceInstanceId})`)
      return
    }
  }

  if (payload.uuid) {
    const {receiveMessageFromDevice, sendAcknowledgement} = useMessageOrchestrator()

    const {shouldProcess} = await receiveMessageFromDevice(payload)

    if (!shouldProcess) {
      console.log(`[app.vue] Message ${payload.uuid} already processed, skipping`)
      return
    }

    try {
      await handlePosChannelMessage(eventName, actualPayload)
      await sendAcknowledgement(payload.uuid, deviceId.value)
    } catch (err) {
      console.error('[app.vue] Failed to process POS message:', err)
    }
  } else {
    console.log('[app.vue] 📨 Backend event (no UUID) - processing:', eventName)
    try {
      await handlePosChannelMessage(eventName, actualPayload)
    } catch (err) {
      console.error('[app.vue] Failed to process backend event:', err)
    }
  }
}

const startOrderSync = async () => {
  const route = useRoute()
  if (route.path.startsWith('/kiosk')) {
    console.log('Order sync skipped: Kiosk mode')
    return
  }

  if (!userStore.activeBusinessId) {
    console.log('Order sync skipped: No active business')
    return
  }

  console.log('Starting initial order sync...')
  try {
    const result = await pullOrdersFromBackend(userStore.activeBusinessId, 24)
    if (result.success) {
      console.log(`Order sync complete: ${result.insertedCount} inserted, ${result.updatedCount} updated, ${result.skippedCount} skipped`)
    }
  } catch (error) {
    console.error('Failed to sync orders:', error)
  }

  if (syncInterval) {
    clearInterval(syncInterval)
  }

  syncInterval = setInterval(async () => {
    if (!userStore.activeBusinessId) {
      console.log('Periodic sync skipped: No active business')
      return
    }
    console.log('Running periodic order sync...')
    try {
      const result = await pullOrdersFromBackend(userStore.activeBusinessId, 1)
      if (result.success) {
        console.log(`Periodic sync: ${result.insertedCount} inserted, ${result.updatedCount} updated, ${result.skippedCount} skipped`)
      }
    } catch (error) {
      console.error('Failed to sync orders:', error)
    }
  }, 5 * 60 * 1000)
}

const initializeGlobalSocket = async () => {
  const route = useRoute()

  if (route.path === '/customer') {
    console.log('[app.vue] ❌ Socket init skipped: Customer display window')
    return
  }

  console.log('[app.vue] initializeGlobalSocket called')
  console.log('[app.vue] userStore.isAuthenticated:', userStore.isAuthenticated)
  console.log('[app.vue] userStore.wsToken:', !!userStore.wsToken)
  console.log('[app.vue] userStore.activeBusinessId:', userStore.activeBusinessId)
  console.log('[app.vue] socket exists:', !!socket)
  console.log('[app.vue] socketStore.isConnected:', socketStore.isConnected)

  if (!userStore.isAuthenticated || !userStore.wsToken || !userStore.activeBusinessId) {
    console.log('[app.vue] ❌ Socket init skipped: User not authenticated or missing required data')
    return
  }

  // Check if socket actually exists and is connected, not just store state
  if (socket?.socket && socket.socket.state === 'open' && socketStore.isConnected) {
    console.log('[app.vue] ✅ Socket already connected')
    return
  }

  // If store says connected but socket doesn't exist, reset the store
  if (socketStore.isConnected && (!socket?.socket || socket.socket.state !== 'open')) {
    console.log('[app.vue] ⚠️ Socket store state desync detected - resetting...')
    socketStore.isConnected = false
    socketStore.isAuthenticated = false
  }

  try {
    console.log('[app.vue] 🔌 Initializing global socket connection...')
    await socket.connect({
      hostname: scConfig.hostname,
      port: scConfig.port,
      secure: scConfig.secure,
    })
    console.log('[app.vue] ✅ Socket connect() completed')

    // Wait a bit for socket to fully authenticate
    setTimeout(async () => {
      if (socketStore.isConnected) {
        console.log('Subscribing to order events...')
        const result = await subscribeToOrderEvents()
        if (result.success) {
          try {
            await fetchInitialOrders()
          } catch (error) {
            console.error('Failed to fetch initial orders:', error)
          }
        }

        deviceId.value = await getInstanceId()
        const isPosDevice = deviceId.value === 'pos'

        if (isPosDevice) {
          console.log('[app.vue] 🗳️ Starting leader election first...')
          await startLeaderElection()
        }

        console.log('Subscribing to channels (hub-and-spoke)...')
        await subscribeToChannels()

        await startOrderSync()

        const {startRetryTimer, performCleanup} = useMessageOrchestrator()
        startRetryTimer()
        performCleanup()
      }
    }, 2000)
  } catch (error) {
    console.error('Failed to initialize global socket:', error)
  }
}

// Watch for authentication changes and connect socket
watch(() => userStore.isAuthenticated, async (isAuthenticated) => {
  if (isAuthenticated && userStore.wsToken && userStore.activeBusinessId) {
    await initializeGlobalSocket()
    await handleDeviceRegistration()
  }
}, {immediate: true})

const handleDeviceRegistration = async () => {
  const route = useRoute()
  if (route.path === '/customer' || isSpecialWindow.value) {
    console.log('[app.vue] Device registration skipped: Special window')
    return
  }

  try {
    console.log('[app.vue] 📱 Starting device registration check...')
    const result = await registerDevice()

    if (!result.success) {
      console.error('[app.vue] Device registration failed:', result.error)
      return
    }

    console.log('[app.vue] Device registration result:', result.status, 'isNew:', result.isNewRegistration)

    if (result.status === 'disabled') {
      console.log('[app.vue] ❌ Device is disabled - logging out')
      showDisabledOverlay.value = true
      await userStore.logout()
      return
    }

    if (result.status === 'pending') {
      console.log('[app.vue] ⏳ Device pending approval - showing overlay and starting poll')
      showPendingApprovalOverlay.value = true
      startPollingForApproval(async (approvalResult) => {
        console.log('[app.vue] ✅ Device approved!')
        showPendingApprovalOverlay.value = false
        if (approvalResult.assignedTerminalId) {
          console.log('[app.vue] Terminal assigned:', approvalResult.assignedTerminalId)
        }
      })
      return
    }

    if (result.status === 'approved') {
      console.log('[app.vue] ✅ Device approved')
      showPendingApprovalOverlay.value = false
      const terminalId = await getAssignedTerminalId()
      if (terminalId) {
        console.log('[app.vue] 💳 Assigned payment terminal:', terminalId)
      }
    }
  } catch (error) {
    console.error('[app.vue] Device registration error:', error)
  }
}

// Watch for wsToken and activeBusinessId. Two roles:
//  1) Initial connect when auth data finishes loading from storage
//     (the original purpose).
//  2) MULTI-BUSINESS SWITCH: when activeBusinessId changes mid-session
//     (a user with access to multiple businesses switches active one),
//     we MUST disconnect the existing socket and reconnect against the
//     new business. Otherwise the socket stays subscribed to
//     `business-{old}-pos` / `business-{old}-orders` and silently misses
//     every event for the new business — orders never appear, KDS
//     doesn't update, etc. The previous code only handled case (1).
watch(() => [userStore.wsToken, userStore.activeBusinessId], async ([wsToken, businessId], oldValue) => {
  const oldBusinessId = oldValue?.[1]
  const businessChanged =
    oldBusinessId != null &&
    businessId != null &&
    oldBusinessId !== businessId

  if (businessChanged) {
    console.log(`[app.vue] 🔁 activeBusinessId changed: ${oldBusinessId} → ${businessId}, reconnecting socket`)
    try {
      socket.disconnect()
    } catch (err) {
      console.warn('[app.vue] Disconnect during business switch failed (non-fatal):', err)
    }
    if (userStore.isAuthenticated && wsToken && businessId) {
      await initializeGlobalSocket()
    }
    return
  }

  if (userStore.isAuthenticated && wsToken && businessId && !socketStore.isConnected) {
    console.log('[app.vue] 🔄 Auth data loaded from storage, initializing socket...')
    await initializeGlobalSocket()
  }
})

onMounted(async () => {
  const route = useRoute()

  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js')
      console.log('[app.vue] Service worker registered:', registration.scope)
    } catch (err) {
      console.log('[app.vue] Service worker registration failed:', err)
    }
  }

  if (!isSpecialWindow.value && window?.location?.hash) {
    console.log('[app.vue] Retry hash navigation on mount:', window.location.hash)
    isSpecialWindow.value = handleHashNavigation()
  }

  if (isSpecialWindow.value || route.path === '/customer') {
    console.log('[app.vue] Special window detected - skipping POS initialization')
    return
  }

  console.log('[app.vue] Mounted - tablet mode:', appIsTabletMode.value, 'isMobile:', isMobile)
  if (isMobile && appIsTabletMode.value) {
    console.log('[app.vue] Tablet detected - forcing horizontal nav')
    configStore.appContentLayoutNav = 'horizontal'
  }

  await initializeGlobalSocket()
  await loadConfiguredPrinters()

  await initSystemWake()
  systemWakeCleanup = onWake(async (wakeInfo) => {
    console.log('[app.vue] 🔔 System wake detected:', wakeInfo)

    if (socket && typeof socket.handleSystemWake === 'function') {
      await socket.handleSystemWake(wakeInfo)
    }

    if (wakeInfo.gapSeconds > 30) {
      console.log('[app.vue] Long sleep detected, refreshing data...')
      try {
        if (userStore.isAuthenticated && userStore.activeBusinessId) {
          await pullOrdersFromBackend(userStore.activeBusinessId, 1)
          console.log('[app.vue] Orders refreshed after wake')
        }
      } catch (err) {
        console.error('[app.vue] Failed to refresh after wake:', err)
      }
    }
  })

  if (userStore.isAuthenticated && userStore.activeBusinessId) {
    try {
      const {invoke} = await import('@tauri-apps/api/core')
      const result = await invoke('show_customer_display')
      console.log('[app.vue] Customer display shown on mount:', result)
    } catch (error) {
      console.log('[app.vue] Customer display not available:', error)
    }
  }
})

onBeforeUnmount(() => {
  if (syncInterval) {
    clearInterval(syncInterval)
  }

  if (systemWakeCleanup) {
    systemWakeCleanup()
    systemWakeCleanup = null
  }

  stopLeaderElection()
  stopPolling()
})
</script>

<template>
  <VLocaleProvider :rtl="configStore.isAppRTL">
    <!-- ℹ️ This is required to set the background color of active nav link based on currently active global theme's primary -->
    <VApp :style="`--v-global-theme-primary: ${hexToRgb(global.current.value.colors.primary)}`">
      <NuxtLayout>
        <NuxtPage/>
      </NuxtLayout>

      <ScrollToTop/>

      <!-- Global Order Notification -->
      <OrderNotification/>

      <!-- Table Request Notification -->
      <TableRequestNotification/>

      <!-- Tab Bill/Payment Notification -->
      <TabBillNotification/>

      <!-- Splash Screen -->
      <SplashScreen/>

      <!-- Version Update Dialog -->
      <VersionUpdateDialog
        :build-timestamp="buildTimestamp"
        :current-version="currentVersion"
        :force-update="forceUpdate"
        :latest-version="latestVersion"
        :show-dialog="showUpdateDialog"
        @dismiss="dismissUpdate"
        @update="applyUpdate"
      />

      <!-- Device Pending Approval Overlay -->
      <VOverlay
        v-model="showPendingApprovalOverlay"
        class="align-center justify-center"
        persistent
        :z-index="9999"
      >
        <VCard class="pa-8 text-center" max-width="500">
          <VIcon class="mdi mb-4" color="warning" size="80">mdi-clock-alert-outline</VIcon>
          <VCardTitle class="text-h5 mb-2">Device Pending Approval</VCardTitle>
          <VCardText>
            <p class="text-body-1 mb-4">
              This device has been registered and is awaiting approval from an administrator.
            </p>
            <p class="text-body-2 text-disabled">
              Please ask your manager to approve this device from the Devices page.
            </p>
            <VProgressLinear class="mt-6" color="warning" indeterminate/>
            <p class="text-caption mt-2">Checking for approval...</p>
          </VCardText>
        </VCard>
      </VOverlay>

      <!-- Device Disabled Overlay -->
      <VOverlay
        v-model="showDisabledOverlay"
        class="align-center justify-center"
        persistent
        :z-index="9999"
      >
        <VCard class="pa-8 text-center" max-width="500">
          <VIcon class="mdi mb-4" color="error" size="80">mdi-cancel</VIcon>
          <VCardTitle class="text-h5 mb-2">Device Disabled</VCardTitle>
          <VCardText>
            <p class="text-body-1 mb-4">
              This device has been disabled by an administrator.
            </p>
            <p class="text-body-2 text-disabled">
              Please contact your manager if you believe this is an error.
            </p>
          </VCardText>
          <VCardActions class="justify-center">
            <VBtn color="primary" variant="tonal" @click="showDisabledOverlay = false; navigateTo('/login')">
              Return to Login
            </VBtn>
          </VCardActions>
        </VCard>
      </VOverlay>
    </VApp>
  </VLocaleProvider>
</template>

<style>
html, body {
  overflow: hidden !important;
  height: 100vh;
  width: 100vw;
}

#__nuxt {
  height: 100vh;
  overflow: hidden;
}
</style>

<style>
@media (max-width: 1024px) {
  .v-application {
    font-size: 0.9em !important;
  }

  .v-card-title,
  .v-list-item-title,
  .v-btn,
  .text-h4,
  .text-h5,
  .text-h6,
  h1, h2, h3, h4, h5, h6 {
    font-weight: 300 !important;
  }

  .font-weight-bold {
    font-weight: 400 !important;
  }

  .font-weight-medium {
    font-weight: 300 !important;
  }
}
</style>
